// src/components/managed-sources/launchPhase.ts

import type { ManagedSource } from "@/types/managedSources";

/**
 * Derives what the wizard's launch screen should show from a managed source's
 * real state. Pure and React-free so every branch below is unit-testable — the
 * original bug on this screen was a hardcoded step index, and the first attempt
 * at fixing it got the state machine wrong, so this deserves its own tests.
 *
 * Backend transitions this mirrors (verified, not assumed):
 *  - ingest is created PROVISIONING/PENDING (`setup_routes.py:1588-1589`);
 *  - backfill_status PENDING → IN_PROGRESS on first run start
 *    (`internal_routes.py:499-501`);
 *  - status → ACTIVE on the first successful run (`pipeline_finalize.py:98-100`);
 *  - backfill_status → COMPLETED once watermark_date reaches the anchor
 *    (`pipeline_finalize.py:181-190`);
 *  - a FAILED run is NOT terminal — it retries with backoff and only pauses
 *    after 3 attempts (`pipeline_finalize.py:44,109-160`);
 *  - transform-only is created ACTIVE/COMPLETED while its first transform is
 *    still running (`setup_routes.py:1563-1564`), so it needs its own track.
 */

/** Overall state of the launch, independent of which step is in progress. */
export type LaunchState =
  /** Normal progress. */
  | "running"
  /** A run failed but the pipeline will try again — not an alarm. */
  | "retrying"
  /** Terminal failure: paused, errored, or the backfill gave up. */
  | "failed"
  /** A run was cancelled: not an error, but nothing is progressing either. */
  | "stopped"
  /** Everything the launch screen tracks is finished. */
  | "done";

export type LaunchPhase = {
  /**
   * 0-based index of the step currently in progress. Equals `stepCount` when
   * every step is finished, which is what renders the timeline fully complete.
   */
  activeIndex: number;
  /** Number of steps this pipeline kind shows. */
  stepCount: number;
  state: LaunchState;
  /**
   * Customer-safe error text, or null. Only ever `error_message`, which the
   * backend maps through `to_user_message` before storing. Not
   * `transform_error`: on a customer response that is one fixed "Modelling
   * failed" sentence (`customer_transform_error`), which the source card
   * already shows.
   */
  errorMessage: string | null;
};

const INGEST_STEPS = 3;
const TRANSFORM_STEPS = 2;

function isTransformOnly(source: ManagedSource): boolean {
  return source.ingest_mode === "TRANSFORM_ONLY";
}

function transformPhase(source: ManagedSource): LaunchPhase {
  const base = { stepCount: TRANSFORM_STEPS, errorMessage: null };

  if (source.last_transform_status === "FAILED") {
    // No message here: the card shows the fixed transform_error itself.
    return { ...base, activeIndex: 1, state: "failed" };
  }
  if (source.last_transform_status === "SUCCESS") {
    return { ...base, activeIndex: TRANSFORM_STEPS, state: "done" };
  }
  // null (queued) or STARTED — the source row already says ACTIVE/COMPLETED at
  // this point, so only last_transform_status can tell us it's still working.
  return {
    ...base,
    activeIndex: source.last_transform_status === "STARTED" ? 1 : 0,
    state: "running",
  };
}

function ingestPhase(source: ManagedSource): LaunchPhase {
  const {
    status,
    backfill_status: backfill,
    last_run_status: lastRun,
    error_message: errorMessage,
  } = source;

  // Which step is in progress, independent of whether it's going well.
  let activeIndex: number;
  if (backfill === "PENDING") {
    activeIndex = 0; // provisioning — Dagster hasn't picked it up yet
  } else if (backfill === "COMPLETED") {
    activeIndex = status === "ACTIVE" ? INGEST_STEPS : 2;
  } else {
    activeIndex = 1; // IN_PROGRESS or FAILED — the backfill step owns it
  }

  const base = { activeIndex, stepCount: INGEST_STEPS };

  // Terminal failures win over progress: a paused pipeline is not "running",
  // however far its steps got.
  if (status === "PAUSED" || status === "ERROR" || backfill === "FAILED") {
    return { ...base, state: "failed", errorMessage: errorMessage ?? null };
  }
  // CANCELLED falls through both branches of the backend's finalizer
  // (`pipeline_finalize.py:86-88`): status stays PROVISIONING and no error is
  // recorded. Without this branch the screen would claim progress forever.
  if (lastRun === "CANCELLED") {
    return { ...base, state: "stopped", errorMessage: null };
  }
  // FAILED but not yet paused: it will retry. Showing a red failure here would
  // be wrong in the common case where the next attempt succeeds.
  if (lastRun === "FAILED") {
    return { ...base, state: "retrying", errorMessage: null };
  }
  if (activeIndex === INGEST_STEPS) {
    return { ...base, state: "done", errorMessage: null };
  }
  return { ...base, state: "running", errorMessage: null };
}

export function launchPhase(source: ManagedSource): LaunchPhase {
  return isTransformOnly(source) ? transformPhase(source) : ingestPhase(source);
}

/**
 * Whether polling can stop. Terminal means "no further backend transition will
 * change what this screen shows" — done, or a failure the pipeline won't retry
 * out of on its own. `retrying` is NOT terminal: that's exactly when the screen
 * has something left to report.
 */
export function isLaunchTerminal(source: ManagedSource): boolean {
  const { state } = launchPhase(source);
  return state === "done" || state === "failed" || state === "stopped";
}
